/* WebComponentToTopButton component */
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging -- disabled to allow to inherit from AbstractComponent */
import { Component, Element, Event, EventEmitter, h, Prop, State } from '@stencil/core'; // eslint-disable-line @typescript-eslint/no-unused-vars
import ToTopButtonTemplate from './cwc-to-top-button.template';
import { AbstractComponent } from '@cewe-designsystem/abstract';
import 'intersection-observer';

@Component({
  tag: 'cwc-to-top-button',
  styleUrl: './cwc-to-top-button.scss',
  shadow: true,
})
export class ToTopButtonComponent {
  /**
   * element
   */
  @Element() element: HTMLElement;

  /**
   * State if the user has scrolled (the scroll position is not the top of the page).
   */
  @State()
  isScrolled = false;

  /**
   * Height of cwc-footnote
   *
   * @docs
   * @property {number} toTopButtonOffset
   */
  @State() toTopButtonOffset = 0;

  /**
   * IntersectionObserver root
   *
   * @docs
   * @property {string} root
   */
  @Prop({ reflect: true }) root: string;

  /**
   * IntersectionObserver rootMargin
   *
   * @docs
   * @property {string} rootMargin
   */
  @Prop({ reflect: true }) rootMargin: string;

  /**
   * IntersectionObserver threshold
   *
   * @docs
   * @property {number} threshold
   */
  @Prop({ reflect: true }) threshold: number;

  /**
   * Elem above which to-top-btn should appear
   * <cwc-footnote class="sg-cw-baseline-content">
   * */
  @Prop() footnoteSelector: string = 'cwc-footnote';

  /**
   * If footnote is positioned static, this class is set.
   * */
  @Prop() footnotePositionClass: string = 'footnote-static';

  /**
   * This is used for the aria-label attribute of the button
   */
  @Prop() label: string;

  /**
   * Event thrown when the user click on to-top button to slide to the beginning of a page
   *
   * @docs
   * @event on:to-top-button:click-to-top
   */
  @Event({
    eventName: 'on:to-top-button:click-to-top',
  })
  onClickToTop: EventEmitter;

  /**
   * This observer manage the button visibility
   */
  intersectionObserver: IntersectionObserver;

  /**
   * This observer calculates the visible part of the footnote when it is static
   * */
  footnoteIntersectionObserver: IntersectionObserver;
  thresholdLength: number = 100;

  _footnoteHeight: number = 0;
  _footnoteIntersectionRatio: number = 0;

  footnoteResizeObserver: ResizeObserver;

  /**
   * @inheritDoc
   */
  connectedCallback() {
    this.initializeIntersectionObservers();
  }

  disconnectedCallback() {
    // stop observers
    this.intersectionObserver.disconnect();

    if (this.footnoteIntersectionObserver) {
      this.footnoteIntersectionObserver.disconnect();
    }
    if (this.footnoteResizeObserver) {
      this.footnoteResizeObserver.disconnect();
    }
  }

  /**
   * Scroll to the top of the page.
   */
  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.resetPageFocus();
    this.onClickToTop.emit(this.element);
  }

  /**
   * This method restores the original state of the page in terms of focus.
   * */
  resetPageFocus() {
    const temporaryElement: HTMLDivElement = document.createElement('div');

    temporaryElement.tabIndex = -1; // user should not be able to focus the element
    temporaryElement.style.position = 'fixed'; // see below for explanation

    /**
     * for this to work properly it is necessary that the temporary element has position fixed, otherwise
     * the animation would be cancelled, since the temporary element would be at the top of the page.
     * */
    document.body.prepend(temporaryElement);
    temporaryElement.focus();

    /**
     * after the reset we don't need the temporary element anymore.
     * */
    temporaryElement.remove();
  }

  /**
   * initializeIntersectionObservers
   */
  initializeIntersectionObservers() {
    const ownerDocument = this.element.ownerDocument,
      // is there a custom selector for the root? if not, take the ownerDocument of this element
      rootNode: Element | Document = this.root ? ownerDocument.querySelector(this.root) : ownerDocument;

    // now create our intersection observer to observe our nasty empty svg to determine a scroll action
    this.intersectionObserver = new IntersectionObserver(
      (root) => {
        // set this boolean variable to use it in our template
        this.isScrolled = root[0].intersectionRatio <= 0;
      },
      {
        root: this.root ? rootNode : null,
        rootMargin: this.rootMargin || '0px',
        threshold: this.threshold || 1.0,
      },
    );

    // let the fun begin and observe our svg right now
    this.intersectionObserver.observe(this._createObservedSvg(ownerDocument, rootNode));

    // observers for calculating toTopButtonOffset
    const footnoteEl: HTMLElement = document.querySelector(this.footnoteSelector);
    if (footnoteEl) {
      this.footnoteIntersectionObserver = new IntersectionObserver(
        (entries: IntersectionObserverEntry[]) => {
          requestAnimationFrame(() => {
            entries.forEach((entry) => {
              this._footnoteIntersectionRatio = entry.isIntersecting ? entry.intersectionRatio : 0;
            });
            this.toTopButtonOffset = Math.floor(this._footnoteHeight * this._footnoteIntersectionRatio);
          });
        },
        {
          root: document,
          rootMargin: '0px',
          threshold: Array.from({ length: this.thresholdLength + 1 }, (_, i) => i / this.thresholdLength),
        },
      );

      this.footnoteIntersectionObserver.observe(footnoteEl);

      this.footnoteResizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
        requestAnimationFrame(() => {
          entries.forEach((entry) => {
            this._footnoteHeight = entry.borderBoxSize[0].blockSize;
          });
          this.toTopButtonOffset = Math.floor(this._footnoteHeight * this._footnoteIntersectionRatio);
        });
      });

      this.footnoteResizeObserver.observe(footnoteEl);
    }
  }

  /**
   * _createObservedSvg
   * @param ownerDocument
   * @param rootNode
   * @private
   */
  _createObservedSvg(ownerDocument, rootNode) {
    // nice id to prevent multiple svg elements
    const elementId = this.element.nodeName.toLowerCase() + '-observed';

    // if we are in a html document grab the body to append child elements otherwise do nothing
    let targetNode = rootNode;
    if (targetNode.nodeName == '#document') {
      targetNode = targetNode.getElementsByTagName('body')[0];
    }

    // do we already have a svg? return it please
    const currentSvg = targetNode.querySelector('#' + elementId);
    if (currentSvg) {
      return currentSvg;
    }

    // if not we have to create a new empty an invisible svg
    const svgElement = ownerDocument.createElement('svg');

    // set id of our observed doomed slave svg and add it to targets begin
    svgElement.setAttribute('id', elementId);
    targetNode.insertBefore(svgElement, targetNode.firstChild);

    return svgElement;
  }

  /**
   * @inheritDoc
   */
  render() {
    return <ToTopButtonTemplate controller={this} />;
  }
}

/**
 * mixin abstract functions
 */
Object.setPrototypeOf(ToTopButtonComponent.prototype, AbstractComponent.prototype);

export interface ToTopButtonComponent extends AbstractComponent {}
